import * as React from 'react'
import { IconButton, useAlert, useEffectWithPolling, WidgetWrapper } from 'uxp/components'
import { IUIPropertyDefinitionMap, IWidgetTemplateInstanceProps, IWidgetTemplateProps, registerCardTemplate } from '../../../common/common'
import { generateIconProp } from '../../../common/icon-selector/IconEditor'
import { SelectableItem, selectUIProps } from '../../../common/SelectableItem'
import { ContainerProperty, extractContainerProperties } from '../../../common/ui-props/ContainerProperty'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TextFieldProperty } from '../../../common/ui-props/TextFieldProperty'
import { SimpleTextProperty } from '../../../common/ui-props/SimpleTextProperty'
import { IBoxProps, IMultipleValueCommonProps, IMultipleValueWidgetProps, MultipleValueCommonProperty, MultipleValueWidgetProperty, SelectablePart } from './MultipleValueWidgetProperty'
import { Conditional } from '../../../common/ConditionalComponent'
import { convertColor, formatNumber, hasValue, replaceWithData } from '../../../../util'
import { IContextData, useInitialContextData } from '../../../../hooks/useContextData'
import { IAction } from '../../../common/action-editor/ActionEditor'
import ActionRenderComponent from '../../../common/components/ActionRenderComponent'
import './styles.scss'
import { executeAndBind } from '../../../common/data-source-picker/datasource'
import { WidgetWrapperWithSidebar } from '../../../common/widget-wrappers/WidgetWrapperWithSidebar'
import WDFilterPanel from '../../../common/components/WDFilterPanel'
import { TextProperty } from '../../../common/ui-props/TextProperty'
import { ColorProperty } from '../../../common/ui-props/ColorProperty'

interface IBoxValue {
    name: string,
    value: number
}

const XPR: React.ForwardRefExoticComponent<React.RefAttributes<IWidgetTemplateInstanceProps> & IWidgetTemplateProps> = React.forwardRef((props, ref) => {
    let uiProps = props?.uiProps || {};
    const containerRef = React.useRef(null)

    const alerts = useAlert()

    const [loading, setLoading] = React.useState(false)
    const [actionData, setActionData] = React.useState<{ action: IAction, contextData: IContextData }>({ action: null, contextData: {} })
    const [contextData, setContextData] = React.useState<IContextData>({})

    type BoxesValues = Array<{ [key: string]: any }>

    let [values, setValues] = React.useState<BoxesValues>([]);
    let [forceRenderCount, setForceRenderCount] = React.useState<number>(0);

    const { isLoading, initialContextData } = useInitialContextData(props)


    let newSampleBox: IBoxProps = {
        label: 'Sample',
        labelColor: '#000000',
        icon: 'chartLine',
        iconColor: '#FFFFFF88',
        fieldName: '',
        defaultValue: '537',
        backgroundColor: '#f4f4f4',
        datasource: null,
        onClickAction: null,
    }

    let defaultFieldProps: IMultipleValueWidgetProps = {
        boxProps: [
            {
                label: 'Incident',
                labelColor: '#000000',
                icon: 'angleUp',
                iconColor: '#f89714',
                fieldName: '',
                defaultValue: '537',
                backgroundColor: '#fffbf3',
                datasource: null,
                onClickAction: null,
            },
            {
                label: 'Visitor',
                labelColor: '#000000',
                icon: 'angleDown',
                iconColor: '#6abc68',
                fieldName: '',
                defaultValue: '341',
                backgroundColor: '#f8fcf9',
                datasource: null,
                onClickAction: null,
            }
        ]
    }

    let CommonProps: IMultipleValueCommonProps = {
        onClickChart: null
    }

    let fieldProps = uiProps.boxFields || uiProps.fields || defaultFieldProps;

    let [data, setData] = React.useState<{ [key: string]: string }>({});

    const XPRProps: IUIPropertyDefinitionMap = {
        "headerContainer": ContainerProperty({
            background: {
                color: "",
                image: {
                    image: "",
                    position: "",
                    size: "",
                },
            }
        }),
        'title': TextProperty({ text: '', color: '', size: '' }, 'Title'),
        'boxFields': MultipleValueWidgetProperty(fieldProps),
        'backgroundColor':ColorProperty('Background Color',{color:''}),
        'CommonProps': MultipleValueCommonProperty(CommonProps),
        'channel': SimpleTextProperty('Channel', ''),
        'refreshInterval': SimpleTextProperty('Refresh Time', '5000'),
    }

    let title = uiProps?.title?.text?.trim() || 'Multiple Value Widget';


    title = replaceWithData(title, contextData)

    let { styles: containerStyles } = extractContainerProperties(uiProps.container);
    let backgroundColor = convertColor(uiProps?.backgroundColor?.color || '{$.theme.primaryBGColor}',contextData);
    let refreshInterval = Number(uiProps?.refreshInterval) || 2000;
    let channel = uiProps?.channel;
    let { onClickChart } = uiProps.CommonProps as IMultipleValueCommonProps || CommonProps;
    const isInDesignerMode = () => props.designer && (props.designer.mode == 'preview' || props.designer.mode == 'thumbnail')

    React.useImperativeHandle(ref, () => ({
        beforeSave: validateBeforeSave,
        onPreview: validateBeforeSave
    }))

    React.useEffect(() => {
        // set these to false rto hide the errors by default
        validateBeforeSave()
    }, [uiProps])

    React.useEffect(() => {
        setLoading(isLoading)
        setContextData(prev => ({ ...prev, ...initialContextData }))
    }, [isLoading, initialContextData])

    function validateBeforeSave() {
        if (!hasDataSourcesConfigured(true)) return false
        if (!hasFieldNamesConfigured(true)) return false
        return true
    }

    function hasDataSourcesConfigured(showError?: boolean) {
        let hasErrors = false
        for (const i in fieldProps?.boxProps) {
            if (!hasValue(fieldProps?.boxProps[i]?.datasource?.source)) {
                if (showError) {
                    // prevent showing multiple errors at once
                    if (hasErrors) continue;

                    props.designer?.addErrors?.([{
                        id: 'field-no-data-source-' + i,
                        type: 'property',
                        error: 'Each box must contain a data source',
                        containerId: 'field-' + i,
                        fieldId: 'field-data-source-' + i
                    }])

                    hasErrors = true
                }
            } else {
                props.designer?.removeErrors?.(['field-no-data-source-' + i])
            }
        }

        return !hasErrors
    }

    function hasFieldNamesConfigured(showError?: boolean) {
        let hasErrors = false
        for (const i in fieldProps?.boxProps) {
            if (!hasValue(fieldProps?.boxProps[i]?.fieldName)) {
                if (showError) {
                    // prevent showing multiple errors at once
                    if (hasErrors) continue;

                    props.designer?.addErrors?.([{
                        id: 'no-field-name-' + i,
                        type: 'property',
                        error: 'Field name is required',
                        containerId: 'field-' + i,
                        fieldId: 'field-name-' + i
                    }])

                    hasErrors = true
                }
            } else {
                props.designer?.removeErrors?.(['no-field-name-' + i])
            }
        }

        return !hasErrors
    }

    useEffectWithPolling(props.uxpContext, channel || "values", refreshInterval || 6000, async () => {
        if (isInDesignerMode()) return;

        await Promise.allSettled(fieldProps?.boxProps?.map((box: IBoxProps, i: number) => {
            return executeAndBind(props.uxpContext, box?.datasource, uiProps.datasources, {}, data)
                .then(res => {
                    if (res) {
                        values[i] = res
                        setValues(values)
                        setForceRenderCount((c) => c + 1)
                    }
                })
        }))
    }, [props.designer]);


    function getFieldValue(fieldName: string, boxIndex: number, boxProp?: IBoxProps) {

        boxProp = boxProp || uiProps.boxFields?.boxProps.find((b: IBoxProps) => b.fieldName === fieldName)
        if (isInDesignerMode() && boxProp.defaultValue) {
            return formatNumber(boxProp.defaultValue);
        }

        if (values[boxIndex]?.[fieldName]) {
            return formatNumber(values[boxIndex]?.[fieldName]);
        }

        return 0
    }

    let handleRemoveBox = (index: number) => {
        if (confirm('Are you sure removing this box')) {
            props?.designer?.setUIProps(Object.assign({}, uiProps, {
                boxFields: {
                    ...fieldProps,
                    boxProps: fieldProps?.boxProps?.filter((p: any, i: any) => i !== index)
                }
            }))
            props.designer?.removeErrors?.(['field-no-data-source-' + index])
            props.designer?.removeErrors?.(['no-field-name-' + index])
        }
    }

    let handleAddNewBox = () => {
        props?.designer?.setUIProps(Object.assign({}, uiProps, {
            boxFields: {
                ...fieldProps,
                boxProps: [...fieldProps?.boxProps, {
                    ...newSampleBox
                }]
            }
        }))
    }

    function handleClickOnBox(e: React.MouseEvent, item: IBoxProps) {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        if (isInDesignerMode()) {
            return;
        }

        setActionData({
            action: item?.onClickAction,
            contextData: { ...contextData }
        })
    }
    function handleChartClick() {

        if (props.designer && (props.designer.mode == 'preview' || props.designer.mode == 'thumbnail')) {
            return
        }
        setActionData({ action: onClickChart || null, contextData: { ...contextData } })
    }
    return (
        <WidgetWrapperWithSidebar
        isInnerField={props.isInnerField}
        containerRef={containerRef}
        designer={props.designer}
        contextData={contextData}
        propDef={XPRProps}
        uiProps={uiProps}
        className='multiple-value-widget-template wd-widget-wrapper'

        filterSection={() => (props.designer?.mode == 'preview' || (Number(uiProps?.filter?.fields?.length) > 0)) ? <WDFilterPanel
            designer={props.designer}
            uxpContext={props.uxpContext}
            uiProps={uiProps}
            containerRef={containerRef}
            data={contextData || {}}
            onChangeFilters={(data) => {
                setContextData(prev => ({ ...prev, filters: data?.filters || {} }))
            }}
            onClearFilters={() => {
                setContextData(prev => ({ ...prev, filters: {} }))
            }}
            onOpenPanel={() => {
                // if (props.designer && (props.designer.mode == 'preview' || props.designer.mode == 'thumbnail')) {
                //     setDisableDrop(true)
                // }
            }}
        // onClosePanel={() => { setDisableDrop(false) }}
        /> : null}
    > 

            <SelectableItem
                style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0 }}
                designer={props.designer}
                props={selectUIProps(uiProps, XPRProps,'backgroundColor', 'container', 'source', 'channel', 'refreshInterval', 'CommonProps')}
                contextData={contextData}
            >
                <div className='container' style={{backgroundColor}}>
                    <Conditional visible={props.designer?.mode == 'preview' || props.designer?.mode == 'thumbnail'}>
                        <IconButton className='plus-icon-button' type='plus' onClick={() => handleAddNewBox()} />
                    </Conditional>

                    
                    {
                        fieldProps.boxProps.length == 0 &&
                        <div className='empty-section'>
                            There's no any field added. Click on the plus icon to add a field
                        </div>
                    }

                    <div className="data-area-container-wrapper">
                        <div className="data-area-container">
                           
                                <div className='data-area' onClick={() => handleChartClick()}>
                                    {
                                        fieldProps.boxProps ? (fieldProps.boxProps as IBoxProps[]).map((boxProp, i) => (

                                            <div className='item-wrapper' key={i} onClick={(e) => handleClickOnBox(e, boxProp)}>
                                                <Conditional visible={props.designer?.mode == 'preview'}>
                                                    <IconButton className='remove-icon' type="delete" onClick={() => handleRemoveBox(i)} />
                                                </Conditional>
                                                <SelectableItem
                                                    designer={props.designer}
                                                    props={selectUIProps(uiProps, XPRProps, 'boxFields')}
                                                    contextData={{ ...contextData, selectedIndex: i, selectedPart: 'all' }}
                                                    elementId={'field-' + i}
                                                >
                                                    <div className="item" style={{ backgroundColor: convertColor(boxProp?.backgroundColor,contextData) }}>
                                                        <div className='icon-wrapper'>
                                                            <SelectableItem
                                                                designer={props.designer}
                                                                props={selectUIProps(uiProps, XPRProps, 'boxFields')}
                                                                contextData={{ ...contextData, selectedIndex: i, selectedPart: 'icon' }}
                                                            >
                                                                <div className='icon-container'>
                                                                    <FontAwesomeIcon className="icon" color={convertColor(boxProp?.iconColor,contextData)} fontWeight={100} icon={generateIconProp(boxProp.icon)} />
                                                                </div>
                                                            </SelectableItem>
                                                        </div>

                                                        <div className='metric-wrapper'>
                                                            <SelectableItem
                                                                designer={props.designer}
                                                                props={selectUIProps(uiProps, XPRProps, 'boxFields')}
                                                                contextData={{ ...contextData, selectedIndex: i, selectedPart: 'value' }}
                                                            >
                                                                <div className={'metric'} style={{ color: convertColor(boxProp?.labelColor,contextData) }}>{getFieldValue(boxProp?.fieldName, i, boxProp)}</div>
                                                            </SelectableItem>
                                                        </div>

                                                        <div className='label-wrapper'>
                                                            <SelectableItem
                                                                designer={props.designer}
                                                                props={selectUIProps(uiProps, XPRProps, 'boxFields')}
                                                                contextData={{ ...contextData, selectedIndex: i, selectedPart: 'label' }}
                                                            >
                                                                <div className='label' style={{ color: convertColor(boxProp?.labelColor,contextData) }}>{boxProp?.label}</div>
                                                            </SelectableItem>
                                                        </div>

                                                    </div>
                                                </SelectableItem>
                                            </div>
                                        )) : <></>
                                    }
                                </div>

                        </div>
                    </div>
                </div>
            </SelectableItem>
            <ActionRenderComponent
                designer={props.designer}
                uxpContext={props.uxpContext}
                {...actionData}
                onClose={() => setActionData({ action: null, contextData: {} })}
                onReloadWidget={() => { }}
            />
        </WidgetWrapperWithSidebar>
    )
})

registerCardTemplate({
    id: 'multiple-value-widget',
    name: 'Multiple Value Widget',
    description: 'This widget can be used to represent multiple values inside boxes.',
    template: XPR,
    icon: generateIconProp('boxes'),
    complexity: 'easy',
    canDragDropFields: true,
});
